/**
 * iter262 — Lien de suivi ponctuel d'un seul dossier, envoyé par SMS.
 * Destiné aux clients qui n'ont pas de suivi global activé sur leur fiche :
 * le lien donne le statut, le devis, les photos et pièces jointes partagées,
 * et la messagerie si on la laisse activée.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquare, Send, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function OrSuiviSmsDialog({ open, onClose, orId, orNumero, plaque, telephone }) {
  const [tel, setTel] = useState(telephone || "");
  const [texte, setTexte] = useState("");
  const [messagerie, setMessagerie] = useState(true);
  const [sending, setSending] = useState(false);
  const [url, setUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/or/${orId}/suivi-sms`, {
        telephone: tel, messagerie, message: texte.trim() || undefined,
      });
      setUrl(data.url);
      if (!texte.trim()) setTexte(`${defaultTexte} ${data.url}`);
      toast.success(
        data.sms === "mocked"
          ? "Lien créé — SMS simulé (envoi réel désactivé en préversion)"
          : `SMS envoyé au ${data.telephone}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setSending(false); }
  };

  const defaultTexte = `Bonjour, voici le lien de suivi de votre véhicule${plaque ? " " + plaque : ""} :`;
  const restant = 160 - (texte.trim() ? texte.length : defaultTexte.length + 60);

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md overflow-hidden [&>*]:min-w-0" data-testid="or-suivi-sms-dialog">
        <DialogHeader>
          <DialogTitle>Envoyer le lien de suivi par SMS</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 min-w-0">
          <p className="text-xs text-gray-500">
            Lien valable pour le dossier <b>{orNumero}</b> uniquement. Le client y voit
            l&apos;avancement, son devis et les fichiers que vous avez partagés.
          </p>
          <div>
            <Label>Mobile du client</Label>
            <Input
              value={tel}
              onChange={(e) => setTel(e.target.value)}
              placeholder="+41 79 123 45 67"
              data-testid="or-suivi-sms-tel"
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between gap-2">
              <Label>Texte du SMS</Label>
              <span className={`text-[10px] ${restant < 0 ? "text-red-600" : "text-gray-400"}`}>
                {restant < 0 ? `${-restant} car. de trop (2 SMS)` : `~${restant} car. restants`}
              </span>
            </div>
            <Textarea
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              rows={3}
              placeholder={`${defaultTexte} <lien>`}
              className="text-sm"
              data-testid="or-suivi-sms-texte"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Laissez vide pour le texte par défaut. Le lien est ajouté automatiquement s&apos;il
              n&apos;apparaît pas dans votre texte.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 p-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-[#52a675]" /> Messagerie
              </div>
              <div className="text-[11px] text-gray-500">Le client peut vous écrire depuis le lien</div>
            </div>
            <Switch checked={messagerie} onCheckedChange={setMessagerie} data-testid="or-suivi-sms-messagerie" />
          </div>
          {url && (
            <div className="rounded-md bg-gray-50 border border-gray-200 p-2 flex items-center gap-2 min-w-0">
              <span className="text-[11px] text-gray-600 truncate flex-1 min-w-0" data-testid="or-suivi-sms-url">{url}</span>
              <Button size="sm" variant="ghost" onClick={copy} data-testid="or-suivi-sms-copy">
                {copied ? <Check className="w-4 h-4 text-[#52a675]" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button
            onClick={send}
            disabled={sending || tel.replace(/\s/g, "").length < 9}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="or-suivi-sms-send"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {url ? "Renvoyer" : "Envoyer le SMS"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
